import Layout from '../../components/Layout';
import ProductComparison from '../../components/ProductComparison';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';

// Vitamin C Serum Comparison
const vitaminCProducts = [
  {
    name: "Standard 15% Vitamin C",
    bestFor: "Most Skin Types",
    affiliateLink: "#",
    features: {
      concentration: "15%",
      stability: true,
      sensitive: false,
      brightening: "High",
      texture: "Lightweight serum",
      fragrance: false,
      beginner: true,
      results: "4-8 weeks"
    }
  },
  {
    name: "Gentle 10% Vitamin C",
    bestFor: "Sensitive Skin",
    affiliateLink: "#",
    features: {
      concentration: "10%",
      stability: true,
      sensitive: true,
      brightening: "Moderate",
      texture: "Cream-serum hybrid",
      fragrance: false,
      beginner: true,
      results: "6-10 weeks"
    }
  },
  {
    name: "Professional 20% Vitamin C",
    bestFor: "Experienced Users",
    affiliateLink: "#",
    features: {
      concentration: "20%",
      stability: true,
      sensitive: false,
      brightening: "Very High",
      texture: "Potent serum",
      fragrance: false,
      beginner: false,
      results: "3-6 weeks"
    }
  }
];

const vitaminCFeatures = [
  { key: "concentration", name: "Concentration", description: "Percentage of L-ascorbic acid" },
  { key: "stability", name: "Stable Formula", description: "Won't oxidize quickly" },
  { key: "sensitive", name: "Sensitive Skin Friendly" },
  { key: "brightening", name: "Brightening Power" },
  { key: "texture", name: "Texture" },
  { key: "fragrance", name: "Fragrance-Free" },
  { key: "beginner", name: "Beginner Friendly" },
  { key: "results", name: "Time to Results" }
];

// Sunscreen Comparison
const sunscreenProducts = [
  {
    name: "Mineral SPF 50",
    bestFor: "Sensitive Skin",
    affiliateLink: "#",
    features: {
      type: "Mineral (Zinc)",
      spf: "SPF 50",
      whitecast: "Minimal",
      sensitive: true,
      reef: true,
      texture: "Lightweight cream",
      reapply: "Every 2 hours",
      makeup: "Good base"
    }
  },
  {
    name: "Chemical SPF 50",
    bestFor: "All Skin Types",
    affiliateLink: "#",
    features: {
      type: "Chemical",
      spf: "SPF 50",
      whitecast: "None",
      sensitive: false,
      reef: false,
      texture: "Ultra-light fluid",
      reapply: "Every 2 hours",
      makeup: "Excellent base"
    }
  },
  {
    name: "Hybrid SPF 50+",
    bestFor: "Oily Skin",
    affiliateLink: "#",
    features: {
      type: "Mineral + Chemical",
      spf: "SPF 50+",
      whitecast: "None",
      sensitive: true,
      reef: true,
      texture: "Matte finish",
      reapply: "Every 2 hours",
      makeup: "Perfect base"
    }
  }
];

const sunscreenFeatures = [
  { key: "type", name: "Filter Type", description: "Mineral vs chemical UV filters" },
  { key: "spf", name: "SPF Level" },
  { key: "whitecast", name: "White Cast" },
  { key: "sensitive", name: "Sensitive Skin Safe" },
  { key: "reef", name: "Reef-Safe" },
  { key: "texture", name: "Texture" },
  { key: "reapply", name: "Reapplication" },
  { key: "makeup", name: "Under Makeup" }
];

// Retinol Comparison
const retinolProducts = [
  {
    name: "Beginner Retinol 0.3%",
    bestFor: "First-Time Users",
    affiliateLink: "#",
    features: {
      strength: "0.3%",
      irritation: "Low",
      results: "Gradual",
      frequency: "2-3x/week",
      encapsulated: true,
      antiaging: "Moderate",
      texture: "Smoothing",
      purging: "Minimal"
    }
  },
  {
    name: "Intermediate Retinol 0.5%",
    bestFor: "Some Experience",
    affiliateLink: "#",
    features: {
      strength: "0.5%",
      irritation: "Moderate",
      results: "Noticeable",
      frequency: "3-4x/week",
      encapsulated: true,
      antiaging: "Good",
      texture: "Refining",
      purging: "Possible"
    }
  },
  {
    name: "Advanced Retinol 1%",
    bestFor: "Experienced Users",
    affiliateLink: "#",
    features: {
      strength: "1%",
      irritation: "Higher",
      results: "Significant",
      frequency: "Daily (built up)",
      encapsulated: true,
      antiaging: "High",
      texture: "Transforming",
      purging: "Likely initially"
    }
  }
];

const retinolFeatures = [
  { key: "strength", name: "Retinol Strength" },
  { key: "irritation", name: "Irritation Risk" },
  { key: "results", name: "Expected Results" },
  { key: "frequency", name: "Recommended Use" },
  { key: "encapsulated", name: "Encapsulated Formula", description: "Slow-release for less irritation" },
  { key: "antiaging", name: "Anti-Aging Effect" },
  { key: "texture", name: "Texture Improvement" },
  { key: "purging", name: "Purging Risk" }
];

const SkincareCompare = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="skincare-compare-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/skincare" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Skincare
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Product Comparison
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Compare Skincare Products
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            Side-by-side comparisons to help you choose the right products for your skin type and concerns. We've done the research so you don't have to.
          </p>
        </div>
      </section>

      {/* Vitamin C Comparison */}
      <section data-testid="vitaminc-comparison" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
              Vitamin C Serums
            </h2>
            <p className="text-[#374151]">
              Choose based on your skin sensitivity and desired intensity.
            </p>
          </div>
          <ProductComparison 
            title="Compare Features"
            products={vitaminCProducts}
            features={vitaminCFeatures}
          />
        </div>
      </section>

      {/* Sunscreen Comparison */}
      <section data-testid="sunscreen-comparison" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
              Sunscreens
            </h2>
            <p className="text-[#374151]">
              Find your perfect SPF based on skin type and preferences.
            </p>
          </div>
          <ProductComparison 
            title="Compare Features"
            products={sunscreenProducts}
            features={sunscreenFeatures}
          />
        </div>
      </section>

      {/* Retinol Comparison */}
      <section data-testid="retinol-comparison" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
              Retinol Products
            </h2>
            <p className="text-[#374151]">
              Start low and go slow—choose based on your experience level.
            </p>
          </div>
          <ProductComparison 
            title="Compare Features"
            products={retinolProducts}
            features={retinolFeatures}
          />
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            How to Use This Comparison
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Start with your skin type:</strong> Sensitive skin should prioritize gentler formulas.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Consider your experience:</strong> New to actives? Start with beginner-friendly options.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Budget matters:</strong> Higher price doesn't always mean better results for your skin.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Patch test first:</strong> Always test new products before full application.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Ready to build your routine?
          </h3>
          <p className="text-[#374151] mb-6">
            Check out our step-by-step skincare guides for morning and evening routines.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/skincare/morning-routines"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A4D39] text-white text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#2A3D29]"
            >
              Morning Routine
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/skincare/evening-routines"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#3A4D39] text-[#3A4D39] text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#3A4D39] hover:text-white"
            >
              Evening Routine
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SkincareCompare;
